import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, List, ListItem, ListItemText, Divider, Alert } from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { setActiveStep, submitOnboarding } from './onboardingSlice';
import StepNav from './StepNav';

const Review = () => {
  const dispatch: AppDispatch = useDispatch();
  const { formData, activeStep, status, error, files, shareholders } = useSelector((state: RootState) => state.onboarding);

  const handleSubmit = () => {
    dispatch(submitOnboarding());
  };

  const handleBack = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  const uploadedFileNames = Object.values(files).filter((f): f is File => f !== null).map(f => f.name);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Review Your Information
      </Typography>
      {status === 'failed' && <Alert severity="error" sx={{ mb: 2 }}>{error || 'Submission failed. Please try again.'}</Alert>}
      <List disablePadding>
        <ListItem>
          <ListItemText primary="Email" secondary={formData.email} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Phone" secondary={formData.phone} />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText primary="Contact Name" secondary={`${formData.contactFirstName} ${formData.contactLastName}`} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Company Name" secondary={formData.companyName} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Company Registration Number" secondary={formData.companyRegistrationNumber} />
        </ListItem>
        <ListItem>
          <ListItemText primary="VAT Number" secondary={formData.vatNumber} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Brand Name" secondary={formData.tradeName} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Statement Descriptor" secondary={formData.statementDescriptor} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Business Description" secondary={formData.businessDescription} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Website" secondary={formData.website || '—'} />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText primary="Address" secondary={`${formData.street}, ${formData.city}, ${formData.province} ${formData.zip}, ${formData.country}`} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Currency" secondary={formData.currency || '—'} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Timezone" secondary={formData.timezone || '—'} />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary="Settlement bank account"
            secondary={[
              formData.bankName ? `Bank: ${formData.bankName}` : null,
              formData.accountNumber ? `Account: ${formData.accountNumber}` : null,
              formData.iban ? `IBAN: ${formData.iban}` : null,
              formData.swift ? `BIC/Swift: ${formData.swift}` : null,
              formData.sortCode ? `Sort code: ${formData.sortCode}` : null,
            ].filter(Boolean).join(' · ') || 'Not provided'}
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText primary="Uploaded Documents" secondary={uploadedFileNames.join(', ') || 'No documents uploaded'} />
        </ListItem>
        {formData.companyType === 'COMPANY' && (
          <>
            <Divider />
            <ListItem>
              <ListItemText primary="Owners" secondary={shareholders.length ? '' : 'No owners added'} />
            </ListItem>
            {shareholders.map((sh, idx) => (
              <ListItem key={idx} sx={{ pl: 4 }}>
                <ListItemText primary={`${sh.firstName} ${sh.lastName}`} secondary={`Role: ${sh.role || '-'} · Share %: ${sh.sharePercent || '-'}`} />
              </ListItem>
            ))}
          </>
        )}
        <Divider />
        <ListItem>
          <ListItemText primary="Confirmations" secondary={
            [
              formData.hasWhatsAppSupportAdded ? 'WhatsApp support added' : null,
              formData.awareDemoMode ? 'Aware of DEMO mode' : null,
            ].filter(Boolean).join(' · ') || 'No confirmations'
          } />
        </ListItem>
      </List>

      <StepNav
        onBack={handleBack}
        onNext={handleSubmit}
        nextLabel={status === 'loading' ? 'Submitting…' : 'Submit'}
        nextDisabled={status === 'loading'}
        backDisabled={status === 'loading'}
        missing={[]}
        showMissing={false}
      />
    </>
  );
};

export default Review;
